import React from 'react';
import { Pill, type PillTone } from './Pill';

/**
 * ConfidencePill: High / Medium / Low label for an extraction confidence score.
 * Scores are 0-1. 0.8 is the CONFIDENCE_THRESHOLD used across the app
 * (FileReview, RecordedDocumentReviewStep): at or above is High.
 * Medium starts at 0.6; anything below is Low.
 * Light theme only.
 */
export const CONFIDENCE_THRESHOLD = 0.8;
export const CONFIDENCE_MEDIUM_FLOOR = 0.6;

export type ConfidenceLevel = 'high' | 'medium' | 'low';

export function confidenceLevel(score: number | null | undefined): ConfidenceLevel {
  if (typeof score !== 'number' || Number.isNaN(score)) return 'low';
  if (score >= CONFIDENCE_THRESHOLD) return 'high';
  if (score >= CONFIDENCE_MEDIUM_FLOOR) return 'medium';
  return 'low';
}

const LEVEL_LABEL: Record<ConfidenceLevel, string> = {
  high: 'High',
  medium: 'Medium',
  low: 'Low',
};

const LEVEL_TONE: Record<ConfidenceLevel, PillTone> = {
  high: 'success',
  medium: 'warning',
  low: 'danger',
};

export interface ConfidencePillProps {
  /** Confidence score from 0 to 1. */
  score: number | null | undefined;
  /** Show the percentage after the label (Review mode), e.g. "High 92%". */
  showPercent?: boolean;
  className?: string;
}

export const ConfidencePill: React.FC<ConfidencePillProps> = ({
  score,
  showPercent = false,
  className,
}) => {
  const level = confidenceLevel(score);
  const hasScore = typeof score === 'number' && !Number.isNaN(score);
  const percent = hasScore ? Math.round((score as number) * 100) : null;
  return (
    <Pill
      tone={LEVEL_TONE[level]}
      className={className}
      title={percent !== null ? `Confidence ${percent}%` : 'Confidence unknown'}
    >
      {LEVEL_LABEL[level]}
      {showPercent && percent !== null ? ` ${percent}%` : null}
    </Pill>
  );
};

export default ConfidencePill;
